/**
 * Libros de códigos de las preguntas abiertas.
 *
 * Preguntas abiertas que se codifican (§9.5): C2, C3, N1, N2, N3, P5, I6, M2, M3.
 *
 * Estrategia de captura: el encuestador escribe la respuesta textual y, cuando
 * existe un libro de códigos cerrado (C2/C3, P5), la PWA ofrece además
 * búsqueda incremental sobre los códigos para codificar en campo. El texto
 * literal SIEMPRE se conserva para permitir la recodificación posterior del
 * validador (§9.5: codificación de abiertas, 3 días post-campo).
 */
/* -------------------------------------------------------------------------- */
/*                   C2 / C3 · Problemas públicos prioritarios                */
/* -------------------------------------------------------------------------- */
/**
 * Códigos exactamente como los lista §Bloque 1 (C2): agua, seguridad,
 * baches/calles, empleo, salud, transporte, basura, alumbrado, corrupción,
 * drogas/adicciones, vivienda/regularización, otro.
 *
 * §8 (piloto) indica ajustar estos códigos tras el piloto; el libro es
 * editable desde el panel de administración.
 */
export const CODEBOOK_PROBLEMAS = {
    id: 'problemas',
    label: 'Problemas públicos prioritarios',
    questions: ['C2', 'C3'],
    allowsOther: true,
    entries: [
        { code: 'agua', label: 'Agua', synonyms: ['agua potable', 'falta de agua', 'desabasto'] },
        {
            code: 'seguridad',
            label: 'Seguridad',
            synonyms: ['inseguridad', 'delincuencia', 'robos', 'violencia', 'policía'],
        },
        {
            code: 'baches_calles',
            label: 'Baches / calles',
            synonyms: ['pavimentación', 'calles', 'baches', 'carreteras'],
        },
        { code: 'empleo', label: 'Empleo', synonyms: ['trabajo', 'desempleo', 'economía', 'salarios'] },
        { code: 'salud', label: 'Salud', synonyms: ['hospital', 'clínica', 'medicinas', 'centro de salud'] },
        {
            code: 'transporte',
            label: 'Transporte',
            synonyms: ['transporte público', 'camiones', 'rutas'],
        },
        { code: 'basura', label: 'Basura', synonyms: ['recolección', 'limpieza', 'tiraderos'] },
        { code: 'alumbrado', label: 'Alumbrado', synonyms: ['alumbrado público', 'luz', 'lámparas'] },
        {
            code: 'corrupcion',
            label: 'Corrupción',
            synonyms: ['corruptos', 'moches', 'transparencia', 'impunidad'],
        },
        {
            code: 'drogas_adicciones',
            label: 'Drogas / adicciones',
            synonyms: ['narcomenudeo', 'adicciones', 'drogadicción'],
        },
        {
            code: 'vivienda_regularizacion',
            label: 'Vivienda / regularización',
            synonyms: ['vivienda', 'regularizar terrenos', 'escrituras', 'predios'],
        },
        { code: 'otro', label: 'Otro (especificar)' },
    ],
};
/* -------------------------------------------------------------------------- */
/*                        P5 · Rechazo partidista duro                        */
/* -------------------------------------------------------------------------- */
export const CODEBOOK_PARTIDOS_RECHAZO = {
    id: 'partidos_rechazo',
    label: 'Partido por el que nunca votaría',
    questions: ['P5'],
    allowsOther: true,
    entries: [
        { code: 'MORENA', label: 'MORENA' },
        { code: 'PVEM', label: 'PVEM', synonyms: ['verde', 'partido verde'] },
        { code: 'PT', label: 'PT', synonyms: ['partido del trabajo'] },
        { code: 'PAN', label: 'PAN', synonyms: ['acción nacional'] },
        { code: 'PRI', label: 'PRI', synonyms: ['revolucionario institucional'] },
        { code: 'MC', label: 'MC', synonyms: ['movimiento ciudadano', 'naranja'] },
        { code: 'PES_BC', label: 'PES BC', synonyms: ['encuentro solidario', 'pes'] },
        { code: 'PAZ', label: 'PAZ' },
        { code: 'SOMOS', label: 'SOMOS' },
        { code: 'PRD', label: 'PRD', synonyms: ['revolución democrática'] },
        { code: 'todos', label: 'Todos / ninguno le gusta' },
        { code: 'ninguno', label: 'Ninguno (votaría por cualquiera)' },
        { code: 'ns_nc', label: 'No sabe / no contestó' },
        { code: 'otro', label: 'Otro (especificar)' },
    ],
};
/* -------------------------------------------------------------------------- */
/*              N1 / N2 / N3 · Menciones espontáneas de personajes            */
/* -------------------------------------------------------------------------- */
/**
 * Las menciones espontáneas se capturan como TEXTO LIBRE y se codifican
 * después. En campo la PWA NO ofrece la lista de personajes, porque
 * sugerir nombres destruye la medición espontánea que es el núcleo
 * estratégico del Bloque 2.
 *
 * Este libro sólo se usa en el panel de codificación del validador.
 */
export const CODEBOOK_MENCIONES_ESPONTANEAS = {
    id: 'menciones_espontaneas',
    label: 'Menciones espontáneas de personajes (codificación de gabinete)',
    questions: ['N1', 'N2', 'N3'],
    allowsOther: true,
    entries: [
        // Los del catálogo se inyectan en tiempo de ejecución desde PERSONAJES
        // para evitar duplicar la lista; ver buildMencionesCodebook().
        { code: 'otro_politico_local', label: 'Otro político local (especificar)' },
        { code: 'politico_estatal', label: 'Político estatal / federal (especificar)' },
        { code: 'nadie', label: 'Nadie' },
        { code: 'ns_nc', label: 'No sabe / no contestó' },
    ],
};
/* -------------------------------------------------------------------------- */
/*                       M1 · Medios de información                          */
/* -------------------------------------------------------------------------- */
export const MEDIOS_OPTIONS = [
    { code: 'facebook', label: 'Facebook' },
    { code: 'instagram', label: 'Instagram' },
    { code: 'tiktok', label: 'TikTok' },
    { code: 'whatsapp', label: 'WhatsApp' },
    { code: 'youtube', label: 'YouTube' },
    { code: 'radio_local', label: 'Radio local' },
    { code: 'television', label: 'Televisión' },
    { code: 'periodico_impreso', label: 'Periódico impreso' },
    { code: 'portal_noticias', label: 'Portal de noticias' },
    { code: 'platicas_conocidos', label: 'Pláticas con conocidos' },
    { code: 'otro', label: 'Otro' },
];
/* -------------------------------------------------------------------------- */
/*                   M4 · Tipo de organización                                */
/* -------------------------------------------------------------------------- */
export const ORGANIZACION_OPTIONS = [
    { code: 'partido', label: 'Partido político' },
    { code: 'comite', label: 'Comité vecinal' },
    { code: 'sindicato', label: 'Sindicato' },
    { code: 'iglesia', label: 'Iglesia / grupo religioso' },
    { code: 'organizacion_vecinal', label: 'Organización vecinal' },
    { code: 'otro', label: 'Otro' },
];
export const CODEBOOKS = [
    CODEBOOK_PROBLEMAS,
    CODEBOOK_PARTIDOS_RECHAZO,
    CODEBOOK_MENCIONES_ESPONTANEAS,
];
/** Inserta los personajes del catálogo en el libro de menciones. */
export function buildMencionesCodebook(personajes) {
    return {
        ...CODEBOOK_MENCIONES_ESPONTANEAS,
        entries: [
            ...personajes.map((p) => ({ code: p.id, label: p.cardName })),
            ...CODEBOOK_MENCIONES_ESPONTANEAS.entries,
        ],
    };
}
//# sourceMappingURL=codebooks.js.map